import { PackageHadDeliveryIssueSubType, PackageHadDeliveryIssueType } from '@consumer/graphql'

// ---------------------------------------------------------------------------
// Monitoring / breadcrumbs
// ---------------------------------------------------------------------------

// Superset: mobile adds 'debug'
export enum BREADCRUMB_TYPES {
  error = 'error',
  info = 'info',
  log = 'log',
  manual = 'manual',
  navigation = 'navigation',
  request = 'request',
  user = 'user',
  warning = 'warning',
  debug = 'debug',
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

// Superset of both apps. Mobile events are more comprehensive.
export enum ANALYTICS_EVENT_TYPES {
  button_pressed = 'button_pressed',
  contact_support = 'contact_support',
  dynamiclink_app_open = 'dynamiclink_app_open',
  tracking_id_searched = 'tracking_id_searched',
  tracking_id_search_error = 'tracking_id_search_error',
  track_package = 'track_package',
  update_delivery = 'update_delivery',
  update_delivery_completion = 'update_delivery_completion',
  update_instructions = 'update_instructions',
  user_phone_auth_success = 'user_phone_auth_success',
  view_order_screen = 'view_order_screen',
  view_external_link = 'view_external_link',
  app_review_submitted = 'app_review_submitted',
  sdk_error = 'sdk_error',
  perfect_placement_photo_saved = 'perfect_placement_photo_saved',
}

// ---------------------------------------------------------------------------
// Firebase SDK (mobile)
// ---------------------------------------------------------------------------

export enum SDK_TYPES {
  firebase_auth = 'firebase_auth',
  firebase_appcheck = 'firebase_appcheck',
}

export enum SDK_ERROR_TYPES {
  auth_internal_error = 'auth_internal_error',
  auth_code_expired = 'auth_code_expired',
  auth_network_request_failed = 'auth_network_request_failed',
}

// ---------------------------------------------------------------------------
// Delivery update flow
// ---------------------------------------------------------------------------

export enum UPDATE_DELIVERY_TYPES {
  reroute = 'reroute',
  update_instructions = 'update_instructions',
}

export enum UPDATE_DELIVERY_STAGES {
  initiated = 'initiated',
  canceled = 'canceled',
  completed = 'completed',
  go_back = 'go_back',
  next = 'next',
}

// ---------------------------------------------------------------------------
// External links
// ---------------------------------------------------------------------------

export enum EXTERNAL_LINK_TYPES {
  internal = 'internal',
  external = 'external',
}

export enum EXTERNAL_LINK_NAMES {
  view_terms = 'view_terms',
  view_privacy_policy = 'view_privacy_policy',
}

// ---------------------------------------------------------------------------
// Feature gates
// Merged from both apps. CWA-prefixed gates are web-only; mobile gates are
// listed with their app context in comments.
// ---------------------------------------------------------------------------

export enum STATSIG_FEATURE_GATES {
  // Mobile
  enableNotificationPreferences = 'consumer-mobile-enable-notification-preferences',
  // CWA
  cwa_show_pkg_location_btn_misdelivered_wrong_location = 'cwa-show-pkg-location-btn-misdelivered-wrong-location',
  cwa_support_escalation = 'cwa_support_escalation',
  cwa_feedback_form = 'cwa_feedback_form',
  three_delivery_photos_on_tracking_page = 'three_delivery_photos_on_tracking_page',
  cwa_access_code_no_code_trigger = 'cwa_access_code_no_code_trigger',
  // Shared: CMA + CWA
  consumer_canonical_log_lines = 'consumer_canonical_log_lines',
}

// ---------------------------------------------------------------------------
// Package events
// ---------------------------------------------------------------------------

export const packageEvents = {
  CREATED: 'created',
  CANCELLED: 'cancelled',
  PICKED_UP_FROM_CLIENT: 'pickedUpFromClient',
  DROPPED_OFF_AT_VEHO: 'droppedOffAtVeho',
  PICKED_UP_FROM_VEHO: 'pickedUpFromVeho',
  DELIVERED: 'delivered',
  DISCARDED: 'discarded',
  RETURNED: 'returned',
  RETURNED_TO_CLIENT: 'returnedToClient',
  RETURNED_TO_VEHO: 'returnedToVeho',
  PENDING: 'pending',
  MISDELIVERED: 'misdelivered',
  NOT_FOUND: 'notFound',
  NOT_RECEIVED_FROM_CLIENT: 'notReceivedFromClient',
  OUT_FOR_PICKUP: 'outForPickup',
  PICKED_UP_FROM_CONSUMER: 'pickedUpFromConsumer',
  PICKUP_SKIPPED: 'pickupSkipped',
  PICKUP_FAILED: 'pickupFailed',
  PICKUP_DROPPED_OFF_AT_VEHO: 'pickupDroppedOffAtVeho',
  PACKAGE_ARRIVED_AT_FACILITY: 'PackageArrivedAtFacility',
  PACKAGE_DEPARTED_FROM_FACILITY: 'PackageDepartedFromFacility',
  PACKAGE_HAD_DELIVERY_ISSUE: 'PackageHadDeliveryIssue',
  PACKAGE_DELAYED: 'PackageDelayed',
  PACKAGE_LOADED_ONTO_FIRST_MILE_TRANSIT: 'PackageLoadedOntoFirstMileTransit',
  WRONG_FACILITY_BY_CLIENT: 'wrongFacilityByClient',
  WRONG_FACILITY_BY_VEHO: 'wrongFacilityByVeho',
} as const

export const errorEvents = [
  packageEvents.CANCELLED,
  packageEvents.DISCARDED,
  packageEvents.RETURNED,
  packageEvents.RETURNED_TO_CLIENT,
  packageEvents.RETURNED_TO_VEHO,
  packageEvents.MISDELIVERED,
  packageEvents.NOT_RECEIVED_FROM_CLIENT,
  packageEvents.PICKUP_SKIPPED,
  packageEvents.PICKUP_FAILED,
  packageEvents.PACKAGE_HAD_DELIVERY_ISSUE,
  packageEvents.PACKAGE_DELAYED,
  packageEvents.WRONG_FACILITY_BY_CLIENT,
  packageEvents.WRONG_FACILITY_BY_VEHO,
]

// ---------------------------------------------------------------------------
// Delivery issue display strings
// Identical between both apps — computed from GraphQL schema enum values.
// ---------------------------------------------------------------------------

export const PackageDelayedIssue = {
  accessIssueFrontGate: `${PackageHadDeliveryIssueType.AccessIssues} - ${PackageHadDeliveryIssueSubType.FrontGate}`,
  accessIssueBuildingOrDoorCode: `${PackageHadDeliveryIssueType.AccessIssues} - ${PackageHadDeliveryIssueSubType.BuildingDoorCode}`,
  accessIssueSchoolCampus: `${PackageHadDeliveryIssueType.AccessIssues} - ${PackageHadDeliveryIssueSubType.SchoolCampus}`,
  wrongLocation: `${PackageHadDeliveryIssueType.WrongLocation}`,
  closedLocationStoreFront: `${PackageHadDeliveryIssueType.ClosedLocation} - ${PackageHadDeliveryIssueSubType.BusinessStoreFront}`,
  closedLocationOffice: `${PackageHadDeliveryIssueType.ClosedLocation} - ${PackageHadDeliveryIssueSubType.BusinessOffice}`,
  closedLocationLeasingOffice: `${PackageHadDeliveryIssueType.ClosedLocation} - ${PackageHadDeliveryIssueSubType.LeasingOffice}`,
  closedLocationMailroom: `${PackageHadDeliveryIssueType.ClosedLocation} - ${PackageHadDeliveryIssueSubType.Mailroom}`,
  customerRefused: `${PackageHadDeliveryIssueType.CustomerRefused}`,
  damagedPackage: `${PackageHadDeliveryIssueType.DamagedPackage}`,
  unsafeConditionsPetOnSite: `${PackageHadDeliveryIssueType.UnsafeConditions} - ${PackageHadDeliveryIssueSubType.PetOnSite}`,
  unsafeConditionsWeatherRelated: `${PackageHadDeliveryIssueType.UnsafeConditions} - ${PackageHadDeliveryIssueSubType.WeatherRelated}`,
  unsafeConditionsDarkUnlitArea: `${PackageHadDeliveryIssueType.UnsafeConditions} - ${PackageHadDeliveryIssueSubType.DarkOrUnlitArea}`,
  other: `${PackageHadDeliveryIssueType.Other}`,
  packageDelayed: 'PackageDelayed',
  ranOutOfTimeRouteStartedLate: `${PackageHadDeliveryIssueType.RanOutOfTime} - ${PackageHadDeliveryIssueSubType.RouteStartedLate}`,
  ranOutOfTimeRouteTookLongerThanEstimated: `${PackageHadDeliveryIssueType.RanOutOfTime} - ${PackageHadDeliveryIssueSubType.RouteTookLongerThanEstimated}`,
  ranOutOfTimeAppIssues: `${PackageHadDeliveryIssueType.RanOutOfTime} - ${PackageHadDeliveryIssueSubType.AppIssues}`,
  militaryBase: `${PackageHadDeliveryIssueType.MilitaryAddress}`,
}

// V2 reasons from the updated packageHadDeliveryIssueV2 semantic event.
// Driver/system-fault reasons (Vehicle, OutOfTime, AppIssue, Personal) are included for
// completeness but map to generic copy on the tracking page.
export const PackageDeliveryIssueV2 = {
  noAccessClosedDoorCode: 'NoAccessClosed - DoorCode',
  noAccessClosedLockedGate: 'NoAccessClosed - LockedGate',
  noAccessClosedClosedAfterHours: 'NoAccessClosed - ClosedAfterHours',
  noAccessClosedMilitaryBase: 'NoAccessClosed - MilitaryBase',
  cantLocateWrongLocation: 'CantLocate - WrongLocation',
  cantLocateMissingUnitNumber: 'CantLocate - MissingUnitNumber',
  safetyAnimalPet: 'Safety - AnimalPet',
  safetyUnlitArea: 'Safety - UnlitArea',
  safetyHostilePerson: 'Safety - HostilePerson',
  vehicleAccident: 'Vehicle - Accident',
  vehicleMalfunction: 'Vehicle - Malfunction',
  weather: 'Weather',
  outOfTime: 'OutOfTime',
  appIssue: 'AppIssue',
  personal: 'Personal',
  // TODO(CBP-4386): CustomerNotAvailable and InvalidDeliveryPin are customer-actionable but lack
  // specific copy; they render generic comms until copy is defined.
  customerNotAvailable: 'CustomerNotAvailable',
  invalidDeliveryPin: 'InvalidDeliveryPin',
  customerRefused: 'CustomerRefused',
  other: 'Other',
}

// ---------------------------------------------------------------------------
// Service types
// ---------------------------------------------------------------------------

export const serviceTypes = {
  PICKUP: 'pickup',
  DELIVERY: 'delivery',
} as const

// ---------------------------------------------------------------------------
// Form states (CWA)
// ---------------------------------------------------------------------------

export const FormStates = {
  PRISTINE: 'pristine',
  SUCCESS: 'success',
  ERROR: 'error',
  SENDING: 'sending',
} as const

// ---------------------------------------------------------------------------
// Order display (mobile)
// ---------------------------------------------------------------------------

export enum EOrderSectionTitle {
  TODAY = 'TODAY',
  UPCOMING = 'UPCOMING',
  PAST = 'PAST',
}

export type TOrderFilters = 'UPCOMING' | 'ALL' | 'DELIVERED' | 'UNDELIVERED'

export const OrderStatusType = {
  created: 'created',
  pending: 'pending',
  notFound: 'notFound',
  pickedUpFromClient: 'pickedUpFromClient',
  droppedOffAtVeho: 'droppedOffAtVeho',
  pickedUpFromVeho: 'pickedUpFromVeho',
  delivered: 'delivered',
  returned: 'returned',
  cancelled: 'cancelled',
  misdelivered: 'misdelivered',
  MiddleMileVehicleArrived: 'MiddleMileVehicleArrived',
  MiddleMileVehicleDeparted: 'MiddleMileVehicleDeparted',
  discarded: 'discarded',
  PackageArrivedAtFacility: 'PackageArrivedAtFacility',
  PackageDepartedFromFacility: 'PackageDepartedFromFacility',
  returnedToClient: 'returnedToClient',
  returnedToVeho: 'returnedToVeho',
  notReceivedFromClient: 'notReceivedFromClient',
  outForPickup: 'outForPickup',
  pickedUpFromConsumer: 'pickedUpFromConsumer',
  pickupSkipped: 'pickupSkipped',
  pickupFailed: 'pickupFailed',
  pickupDroppedOffAtVeho: 'pickupDroppedOffAtVeho',
  issueWithOrder: 'issueWithOrder',
  seeMore: 'seeMore',
} as const

// ---------------------------------------------------------------------------
// Mobile storage keys
// ---------------------------------------------------------------------------

export enum storageKeys {
  PUSH_NOTIFICATIONS_TOKEN = 'PUSH_NOTIFICATIONS_TOKEN',
  FEATURE_FLAGS = 'FEATURE_FLAGS',
  FEATURE_FLAGS_DUPE = 'FEATURE_FLAGS_DUPE',
  APOLLO_CACHE_VERSION = 'APOLLO_CACHE_VERSION',
}

// ---------------------------------------------------------------------------
// Mobile photo options
// ---------------------------------------------------------------------------

export enum PlacementPhotoOptions {
  photoLibrary = 'Photo Library',
  takePhoto = 'Take Photo',
  chooseFile = 'Choose File',
  deletePhoto = 'Delete Photo',
}
